import { useState } from 'react';
import { Mail } from 'lucide-react';
import { Button, ButtonLink } from './primitives';

/**
 * The one "Send by email" control that sits beside every "Copy" for a link.
 * (Flow upgrade, 6 Oct 2026: "One small shared component used in all four, not
 * four implementations". Product owner, later: "should just use a mailto, with
 * a ready to use draft".)
 *
 * It opens the person's own mail program with a finished draft — recipient,
 * subject and body filled in — and sends nothing itself. It used to ask the
 * server to send the mail, which does nothing useful wherever no mail transport
 * is configured (production has none), and "Send" then failed after the click.
 * A `mailto:` needs nothing from the server, and the person sees and can change
 * exactly what goes out under their own name.
 *
 * Deliberately presentational, like `Dialog` (see that component's own
 * comment): no i18n import, no API call. A caller passes the label and the
 * wording of the draft, already in the right language, so this file works the
 * same inside the main app (which has an I18nProvider) or anywhere else that
 * does not. The wording is the caller's, because it differs by what is being
 * shared — a vendor portal link, a contributor's invitation, a read-only
 * ranking — and the control cannot know any of that.
 *
 * ONE LINK, ONE PERSON. "Each recipient has their own address... the link IS
 * the credential": the control takes a single `recipient`, the one person the
 * caller already knows the link is for, or none. It never accepts a list.
 *
 * THE CONTRIBUTOR PIN NEVER TRAVELS IN THE MAIL. A seat's first link is issued
 * together with an access code, and a code that goes in the same message as
 * the link protects nothing — anyone holding both can open the card. That rule
 * used to be enforced by what this control could carry (a link, and a note the
 * Owner typed). Now the caller writes the wording, so the rule has to be a
 * property of the control instead of a habit of its callers: a caller names
 * the secrets that must not travel in `withhold`, and if any of them turns up
 * in the draft the control builds no mail at all and shows a disabled button.
 * Failing closed is the point — a draft that cannot be built is a visible bug
 * in the caller; a draft that leaks a code is a silent one. SeatsView passes
 * the PIN; its wording says only that a code will come separately.
 *
 * Copy is not this component's business, and is unaffected: the caller renders
 * the link and its Copy button beside this, and that is the fallback when a
 * device has no mail program set up — see `fallbackNote`.
 */

export type MailDraft = {
  subject: string;
  /** Paragraphs before the link: the greeting, and what this is. */
  intro: string[];
  /** The link itself — the last thing to be cut if the draft is too long. Empty for a draft that has none (the site's contact enquiry). */
  link: string;
  /** Paragraphs after the link: what the person is asked to do. */
  ask: string[];
};

const CRLF = '\r\n';

/**
 * Some mail clients and browsers give up on a `mailto:` URL of a few thousand
 * characters (it ends up in a command line or a URL handler, each with its own
 * limit). 2,000 is the conservative common ground. Drafts here are a few lines,
 * so this is a guard against a pasted-in essay or an unusually long link, not
 * something ordinary use reaches.
 */
const MAX_MAILTO_LENGTH = 2000;

/** Paragraphs joined by a blank line, with CRLF breaks throughout (RFC 6068 asks for it). */
export function composeBody(draft: MailDraft, opts: { linkOnly?: boolean } = {}): string {
  const paragraphs = opts.linkOnly ? [draft.link] : [...draft.intro, draft.link, ...draft.ask];
  return paragraphs
    .map((paragraph) => paragraph.trim().replace(/\r?\n/g, CRLF))
    .filter(Boolean)
    .join(CRLF + CRLF);
}

/**
 * `@` is left as it is in the address: RFC 6068 allows it there and some
 * clients mishandle %40. Everything else is percent-encoded, which turns a
 * CRLF into %0D%0A and keeps `&`, `#`, `?` and spaces from ending a field early.
 */
export function buildMailto(to: string, subject: string, body: string): string {
  const address = encodeURIComponent(to.trim()).replace(/%40/g, '@');
  return `mailto:${address}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** The URL for a draft, shortened to just the link if the full wording will not fit. */
export function mailtoFor(to: string, draft: MailDraft): string {
  const full = buildMailto(to, draft.subject, composeBody(draft));
  if (full.length <= MAX_MAILTO_LENGTH) return full;
  return buildMailto(to, draft.subject, composeBody(draft, { linkOnly: true }));
}

/** True when the draft mentions anything the caller said must not travel. */
function leaksSecret(draft: MailDraft, withhold: string[]): boolean {
  const text = [draft.subject, ...draft.intro, draft.link, ...draft.ask].join('\n');
  return withhold.map((secret) => secret.trim()).filter(Boolean).some((secret) => text.includes(secret));
}

export function SendLinkControl({
  label,
  recipient,
  draft,
  withhold = [],
  fallbackNote,
  variant = 'secondary',
  onSend,
}: {
  label: string;
  /** The one person the caller already knows this link is for, if anyone. */
  recipient?: string;
  draft: MailDraft;
  /** Secrets (a contributor's PIN) that must never appear in the draft. */
  withhold?: string[];
  /** Shown after the click: what to do if no mail program opened. */
  fallbackNote?: string;
  /** The button's weight: secondary beside a Copy, primary where this is the page's one action. */
  variant?: 'primary' | 'secondary';
  /**
   * Told when the person opens the draft — for a caller that wants to note it
   * (the site's contact page files an enquiry row). Fire-and-forget by
   * contract: it must not be awaited, and whatever it does — or throws — can
   * never delay or prevent the mail program opening, which already has.
   */
  onSend?: () => void;
}) {
  const [clicked, setClicked] = useState(false);

  if (leaksSecret(draft, withhold)) {
    return (
      <Button variant={variant} icon={<Mail size={15} />} disabled>
        {label}
      </Button>
    );
  }

  return (
    <div className="send-control">
      <ButtonLink
        variant={variant}
        icon={<Mail size={15} />}
        href={mailtoFor(recipient || '', draft)}
        onClick={() => {
          setClicked(true);
          try {
            onSend?.();
          } catch {
            // The draft is already opening; a failing observer is not the person's problem.
          }
        }}
      >
        {label}
      </ButtonLink>
      {clicked && fallbackNote ? <p className="subtle">{fallbackNote}</p> : null}
    </div>
  );
}

export default SendLinkControl;
